import * as React from 'react'
import {
  RiskChallenge,
  RiskActions,
  RiskContext,
  RiskWorkflows,
} from '@doordash/component-risk'
import t from './strings'
import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { get, isEmpty, noop } from 'lodash'
import {
  ArchiveAccountState,
  Channel,
  PrivacyMode as Mode,
  RTIPrivacyFlow,
  RTFPrivacyFlow,
} from '../../../models/types'
import { Text, StackChildren, Inset } from '@doordash/design-language'
import Button from '@doordash/component-button'
import ArchiveAccountSection from '../../molecules/archive-account-section'
import RequestArchive from '../../molecules/request-archive'
import PreparingArchive from '../../molecules/preparing-archive'
import RequestDeletion from './RequestDeletion'
import ConfirmDeletion from './ConfirmDeletion'
import ExitDeletion from './ExitDeletion'
import * as api from '../../../api'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import BlockDeletion from './BlockDeletion'
import { loadUrl } from '../../../webview'
import { isWebview } from '../../../utilities/userAgent'
import ErrorPage from '../../molecules/error-boundary/error-page'
import { CurrencyCode, getDefaultCurrencySymbol } from '@doordash/lib-i18n'

type Props = {
  trackGDPR?: (data: DA.RTI) => void
  trackRTF?: (data: DA.RTF) => void
}

const PAGE_NAME = 'block-deletion'
const PRIVACY_POLICY_URL = 'https://www.doordash.com/dasherprivacypolicy/'

const handlePrivacyPolicyClick = () => {
  if (isWebview()) {
    loadUrl(PRIVACY_POLICY_URL)
  } else {
    window.location.assign(PRIVACY_POLICY_URL)
  }
}

export const ManageAccount: React.FC<Props> = ({ 
  trackGDPR = noop,
  trackRTF = noop,
}) => {
  const [archiveAccountState, setArchiveAccountState] = useState<
    ArchiveAccountState
  >()
  const { dispatch } = React.useContext(RiskContext)
  const [archiveRequestId, setArchiveRequestId] = useState<string>('')
  const [archiveRequestDate, setArchiveRequestDate] = useState<string>('')
  const [archiveExpiryDate, setArchiveExpiryDate] = useState<string>('')
  const [archiveDownloadLink, setArchiveDownloadLink] = useState<string>('')
  const [unpaidBalance, setUnpaidBalance] = useState<string>('')
  const [requestMode, setRequestMode] = useState<
    RTIPrivacyFlow | RTFPrivacyFlow
  >()

  const selectMode = (selectedMode: Mode) => {
    trackGDPR({ action: DA.GDPRActions.MfaRender })

    dispatch({
      type: RiskActions.RegisterCallback,
      onClose: () => {
        trackGDPR({ action: DA.GDPRActions.MfaClose })
        setRequestMode(null)
      },
      onSuccess: () => {
        trackGDPR({ action: DA.GDPRActions.MfaSuccess })
        setRequestMode(
          selectedMode === Mode.RTI
            ? RTIPrivacyFlow.Request
            : RTFPrivacyFlow.Request
        )
      },
    })

    dispatch({
      type: RiskActions.VerificationRequired,
      workflow: RiskWorkflows.DasherLogin,
    })
  }

  const handleRTFOnClick = async () => {
    try {
      const unpaidTransactionsResponse = await api.getTotalUnpaidTransactionsForDasherHandler()
      const unpaidTransactions = get(unpaidTransactionsResponse, 'data.unpaid_transactions')
      if (!isEmpty(unpaidTransactions)) {
        const unpaidCurrency = unpaidTransactions[0].currency.toUpperCase() as CurrencyCode || CurrencyCode.USD
        const unpaidAmount = unpaidTransactions.reduce((totalUnpaidAmount, unpaidTransaction) => totalUnpaidAmount += unpaidTransaction.amount,0)
        const unpaidBalance = `${getDefaultCurrencySymbol(unpaidCurrency)}${unpaidAmount/100}`
        setUnpaidBalance(unpaidBalance)
        setRequestMode(RTFPrivacyFlow.Block)
      } else {
        selectMode(Mode.RTF)
      }
    } catch (error) {
      trackRTF({ action: DA.GDPRActions.Error, name: PAGE_NAME })
      setRequestMode(RTFPrivacyFlow.Error)
    }
  }

  useEffect(() => {
    const fetchAndParseData = async () => {
      try {
        const rtiRequestResponse = await api.getDasherRTIRequestList()
        const requests = rtiRequestResponse.data.requests

        if (requests.length === 0) {
          setArchiveAccountState(ArchiveAccountState.NoStatus)
          return
        }

        const pendingRequests = requests.filter(
          req => req.status === 'IN_PROGRESS'
        )
        if (pendingRequests.length > 0) {
          const pendingRequest = pendingRequests[0]
          setArchiveAccountState(ArchiveAccountState.Pending)
          setArchiveRequestDate(pendingRequest.created_at)
          return
        }

        const completedRequests = requests.filter(
          req => req.status === 'COMPLETE'
        )
        if (completedRequests.length > 0) {
          const completedRequest = completedRequests[0]
          const { data: archiveLink } = await api.getDasherRTIRequestArchive(
            completedRequest.request_id
          )
          setArchiveAccountState(ArchiveAccountState.Ready)
          setArchiveRequestDate(completedRequest.created_at)
          setArchiveExpiryDate(completedRequest.expires_at)
          setArchiveDownloadLink(archiveLink.url)
          setArchiveRequestId(completedRequest.request_id)
        }
      } catch (error) {
        console.log(error)
        setArchiveAccountState(ArchiveAccountState.NoStatus)
      }
    }

    fetchAndParseData()
  }, [])

  return (
    <React.Fragment>
      <RiskChallenge channel={Channel.Sms} />
      <Inset>
        {(requestMode === RTIPrivacyFlow.Request) && (
          <RequestArchive setView={setRequestMode} />
        )}
        {requestMode === RTIPrivacyFlow.PreparingArchive && (
          <PreparingArchive />
        )}
        {requestMode === RTFPrivacyFlow.Request && (
          <RequestDeletion setView={setRequestMode} handlePrivacyPolicyClick = {handlePrivacyPolicyClick} />
        )}
        {requestMode === RTFPrivacyFlow.Confirm && (
          <ConfirmDeletion setView={setRequestMode} handlePrivacyPolicyClick = {handlePrivacyPolicyClick} />
        )}
        {requestMode === RTFPrivacyFlow.Exit && <ExitDeletion />}
        {requestMode === RTFPrivacyFlow.Block && <BlockDeletion unpaidBalance={unpaidBalance}/>}
        {requestMode === RTFPrivacyFlow.Error && <ErrorPage shouldShowDismiss={false}/>}
        {!requestMode && (
          <StackChildren size={StackChildren.Sizes.XLarge}>
            <Text styles={Text.Styles.PageTitle2}>{t.header()}</Text>
            <StackChildren size={StackChildren.Sizes.XxLarge}>
              <ArchiveAccountSection
                archiveRequestId={archiveRequestId}
                archiveAccountState={archiveAccountState}
                archiveRequestDate={archiveRequestDate}
                archiveExpiryDate={archiveExpiryDate}
                archiveDownloadLink={archiveDownloadLink}
                onModeChange={selectMode}
              />
              <StackChildren size={StackChildren.Sizes.XxSmall}>
                <Text styles={Text.Styles.Label1Emphasis}>
                  {t.deleteAccountHeader()}
                </Text>
                <Text styles={Text.Styles.Body1}>{t.deleteAccountBody()}</Text>
                <Button
                  type={Button.Types.Tertiary}
                  size={Button.Sizes.Small}
                  isInline
                  onClick={() => {
                    handleRTFOnClick()
                  }}
                >
                  {t.deleteAccountButton()}
                </Button>
              </StackChildren>
            </StackChildren>
          </StackChildren>
        )}
      </Inset>
    </React.Fragment>
  )
}

const mapDispatchToProps = {
  trackGDPR: events.trackGDPR,
  trackRTF: events.trackRTF,
}

export default connect(null, mapDispatchToProps)(ManageAccount)